import React from 'react';
import {
    Document, Page, Text, View, StyleSheet, Image, pdf,
} from '@react-pdf/renderer';

const BLUE = '#1976d2';
const DARK = '#1a237e';
const GRAY = '#6b7280';
const LIGHT = '#f3f4f6';
const GREEN = '#2e7d32';
const ORANGE = '#ed6c02';
const PURPLE = '#9c27b0';
const WHITE = '#ffffff';

const styles = StyleSheet.create({
    page: { fontFamily: 'Helvetica', backgroundColor: WHITE, paddingHorizontal: 36, paddingVertical: 32 },
    // Header
    header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, borderBottomWidth: 2, borderBottomColor: BLUE, paddingBottom: 12 },
    logo: { width: 80, height: 36, objectFit: 'contain' },
    headerRight: { alignItems: 'flex-end' },
    title: { fontSize: 16, fontFamily: 'Helvetica-Bold', color: DARK },
    subtitle: { fontSize: 9, color: GRAY, marginTop: 2 },
    // Section
    sectionTitle: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: DARK, marginBottom: 8, marginTop: 16, borderLeftWidth: 3, borderLeftColor: BLUE, paddingLeft: 6 },
    // KPI cards
    kpiRow: { flexDirection: 'row', gap: 8, marginBottom: 4 },
    kpiCard: { flex: 1, backgroundColor: LIGHT, borderRadius: 6, padding: 10, borderLeftWidth: 3 },
    kpiLabel: { fontSize: 8, color: GRAY, marginBottom: 3 },
    kpiValue: { fontSize: 15, fontFamily: 'Helvetica-Bold', color: DARK },
    kpiSub: { fontSize: 7.5, color: GRAY, marginTop: 2 },
    // Bar chart
    chartContainer: { marginBottom: 4 },
    barRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
    barLabel: { fontSize: 8, color: DARK, width: 90 },
    barBg: { flex: 1, height: 10, backgroundColor: '#e5e7eb', borderRadius: 3, overflow: 'hidden' },
    barFill: { height: 10, borderRadius: 3 },
    barValue: { fontSize: 8, color: GRAY, width: 36, textAlign: 'right' },
    // Trend
    trendRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 6, height: 60, marginBottom: 4 },
    trendCol: { flex: 1, alignItems: 'center' },
    trendBar: { width: '100%', borderRadius: 2 },
    trendLabel: { fontSize: 7, color: GRAY, marginTop: 3 },
    trendValue: { fontSize: 6.5, color: BLUE, marginBottom: 2 },
    // Charts grid
    chartsRow: { flexDirection: 'row', gap: 10 },
    chartBox: { flex: 1, backgroundColor: LIGHT, borderRadius: 6, padding: 10 },
    chartTitle: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: DARK, marginBottom: 6 },
    // Table
    tableHeader: { flexDirection: 'row', backgroundColor: BLUE, borderRadius: 3, paddingVertical: 5, paddingHorizontal: 4, marginBottom: 2 },
    tableHeaderCell: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: WHITE },
    tableRow: { flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: '#e5e7eb', paddingVertical: 4, paddingHorizontal: 4 },
    tableRowAlt: { backgroundColor: '#f9fafb' },
    tableCell: { fontSize: 7.5, color: DARK },
    // Pill
    pill: { borderRadius: 8, paddingHorizontal: 5, paddingVertical: 1, alignSelf: 'flex-start' },
    pillText: { fontSize: 7, fontFamily: 'Helvetica-Bold' },
    // Footer
    footer: { position: 'absolute', bottom: 20, left: 36, right: 36, flexDirection: 'row', justifyContent: 'space-between', borderTopWidth: 0.5, borderTopColor: '#e5e7eb', paddingTop: 6 },
    footerText: { fontSize: 7.5, color: GRAY },
});

const COL_WIDTHS = { id: '8%', fecha: '11%', donante: '18%', email: '19%', ciudad: '10%', tipo: '11%', metodoPago: '9%', monto: '8%', estado: '6%' };

function kpiColor(index) {
    return [BLUE, GREEN, ORANGE, PURPLE][index % 4];
}

function BarChart({ data, color, maxValue }) {
    const max = maxValue || Math.max(...(data || []).map(d => d.value), 1);
    return (
        <View style={styles.chartContainer}>
            {(data || []).slice(0, 6).map((item, i) => (
                <View key={i} style={styles.barRow}>
                    <Text style={styles.barLabel} numberOfLines={1}>{item.label}</Text>
                    <View style={styles.barBg}>
                        <View style={[styles.barFill, { width: `${Math.round((item.value / max) * 100)}%`, backgroundColor: color }]} />
                    </View>
                    <Text style={styles.barValue}>{item.value.toLocaleString('es-BO')}</Text>
                </View>
            ))}
        </View>
    );
}

function TrendChart({ data }) {
    if (!data || data.length === 0) return null;
    const max = Math.max(...data.map(d => d.total), 1);
    return (
        <View style={styles.trendRow}>
            {data.map((d, i) => {
                const pct = Math.max((d.total / max) * 100, 4);
                return (
                    <View key={i} style={styles.trendCol}>
                        <Text style={styles.trendValue}>{d.total >= 1000 ? `${(d.total / 1000).toFixed(1)}k` : d.total}</Text>
                        <View style={[styles.trendBar, { height: pct * 0.52, backgroundColor: BLUE, opacity: 0.7 + (i / data.length) * 0.3 }]} />
                        <Text style={styles.trendLabel}>{d.month}</Text>
                    </View>
                );
            })}
        </View>
    );
}

function StatusPill({ value }) {
    const map = {
        Completada: { bg: '#dcfce7', color: GREEN },
        Pendiente: { bg: '#fef9c3', color: '#92400e' },
        Fallida: { bg: '#fee2e2', color: '#b91c1c' },
    };
    const s = map[value] || { bg: LIGHT, color: DARK };
    return (
        <View style={[styles.pill, { backgroundColor: s.bg }]}>
            <Text style={[styles.pillText, { color: s.color }]}>{value}</Text>
        </View>
    );
}

function KPISection({ summary }) {
    const cards = [
        { label: 'Total recaudado', value: `Bs. ${(summary.totalAmount || 0).toLocaleString('es-BO')}`, sub: `+${summary.amountGrowth || 0}% vs periodo anterior` },
        { label: 'Total donaciones', value: (summary.totalDonations || 0).toLocaleString(), sub: `+${summary.donationsGrowth || 0}% vs periodo anterior` },
        { label: 'Donantes únicos', value: (summary.uniqueDonors || 0).toLocaleString(), sub: `Prom. Bs. ${summary.avgDonation || 0}` },
        { label: 'Donaciones recurrentes', value: (summary.recurringDonations || 0).toLocaleString(), sub: `${summary.recurringPct || 0}% del total` },
    ];
    return (
        <View style={styles.kpiRow}>
            {cards.map((c, i) => (
                <View key={i} style={[styles.kpiCard, { borderLeftColor: kpiColor(i) }]}>
                    <Text style={styles.kpiLabel}>{c.label}</Text>
                    <Text style={styles.kpiValue}>{c.value}</Text>
                    <Text style={styles.kpiSub}>{c.sub}</Text>
                </View>
            ))}
        </View>
    );
}

function TableSection({ rows }) {
    const cols = [
        { key: 'id', label: 'ID', w: COL_WIDTHS.id },
        { key: 'fecha', label: 'Fecha', w: COL_WIDTHS.fecha },
        { key: 'donante', label: 'Donante', w: COL_WIDTHS.donante },
        { key: 'email', label: 'Email', w: COL_WIDTHS.email },
        { key: 'ciudad', label: 'Ciudad', w: COL_WIDTHS.ciudad },
        { key: 'tipo', label: 'Tipo', w: COL_WIDTHS.tipo },
        { key: 'metodoPago', label: 'Método', w: COL_WIDTHS.metodoPago },
        { key: 'monto', label: 'Monto', w: COL_WIDTHS.monto },
        { key: 'estado', label: 'Estado', w: COL_WIDTHS.estado },
    ];
    return (
        <View>
            <View style={styles.tableHeader}>
                {cols.map(c => (
                    <Text key={c.key} style={[styles.tableHeaderCell, { width: c.w }]}>{c.label}</Text>
                ))}
            </View>
            {(rows || []).slice(0, 40).map((row, i) => (
                <View key={i} style={[styles.tableRow, i % 2 === 1 ? styles.tableRowAlt : {}]}>
                    {cols.map(c => (
                        <View key={c.key} style={{ width: c.w }}>
                            {c.key === 'estado'
                                ? <StatusPill value={row[c.key]} />
                                : <Text style={styles.tableCell} numberOfLines={1}>
                                    {c.key === 'monto' ? `Bs. ${Number(row[c.key]).toFixed(0)}` : String(row[c.key] ?? '')}
                                  </Text>
                            }
                        </View>
                    ))}
                </View>
            ))}
            {(rows || []).length > 40 && (
                <Text style={[styles.footerText, { marginTop: 4 }]}>
                    Mostrando 40 de {rows.length} registros. Descarga el Excel para el detalle completo.
                </Text>
            )}
        </View>
    );
}

function DashboardDocument({ summary, rows, appliedFilters }) {
    const now = new Date();
    const generatedAt = now.toLocaleString('es-BO', { dateStyle: 'long', timeStyle: 'short' });
    const periodLabel = appliedFilters
        ? `${appliedFilters.dateFrom}${appliedFilters.dateFromTime ? ' ' + appliedFilters.dateFromTime : ''} → ${appliedFilters.dateTo}${appliedFilters.dateToTime ? ' ' + appliedFilters.dateToTime : ''}`
        : '';

    return (
        <Document title="Dashboard de Donaciones — UNICEF Bolivia" author="UNICEF Bolivia">
            {/* Page 1 — KPIs + charts */}
            <Page size="A4" orientation="landscape" style={styles.page}>
                {/* Header */}
                <View style={styles.header}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                        <Image src="/assets/imgs/unicef/Logo.png" style={styles.logo} />
                        <View>
                            <Text style={styles.title}>Dashboard de Donaciones</Text>
                            <Text style={styles.subtitle}>UNICEF Bolivia</Text>
                        </View>
                    </View>
                    <View style={styles.headerRight}>
                        <Text style={[styles.subtitle, { fontFamily: 'Helvetica-Bold', color: DARK }]}>Período</Text>
                        <Text style={styles.subtitle}>{periodLabel}</Text>
                        <Text style={[styles.subtitle, { marginTop: 2 }]}>Generado: {generatedAt}</Text>
                    </View>
                </View>

                {/* KPI cards */}
                <Text style={styles.sectionTitle}>Indicadores principales</Text>
                <KPISection summary={summary} />

                {/* Trend + by type + by payment */}
                <Text style={styles.sectionTitle}>Tendencia mensual y distribución</Text>
                <View style={styles.chartsRow}>
                    <View style={[styles.chartBox, { flex: 2 }]}>
                        <Text style={styles.chartTitle}>Tendencia mensual (Bs. recaudados)</Text>
                        <TrendChart data={summary.monthlyTrend} />
                    </View>
                    <View style={styles.chartBox}>
                        <Text style={styles.chartTitle}>Por tipo de donación</Text>
                        <BarChart data={summary.byType} color={BLUE} />
                    </View>
                    <View style={styles.chartBox}>
                        <Text style={styles.chartTitle}>Por método de pago</Text>
                        <BarChart data={summary.byPaymentMethod} color={ORANGE} />
                    </View>
                </View>

                {/* City + status + campaign */}
                <View style={[styles.chartsRow, { marginTop: 10 }]}>
                    <View style={styles.chartBox}>
                        <Text style={styles.chartTitle}>Por ciudad (top 6)</Text>
                        <BarChart data={summary.byCity} color={GREEN} />
                    </View>
                    <View style={styles.chartBox}>
                        <Text style={styles.chartTitle}>Por estado</Text>
                        <BarChart data={summary.byStatus} color={PURPLE} />
                    </View>
                    <View style={[styles.chartBox, { flex: 2 }]}>
                        <Text style={styles.chartTitle}>Top campañas por monto recaudado (Bs.)</Text>
                        <BarChart data={summary.byCampaign} color='#0288d1' />
                    </View>
                </View>

                {/* Footer */}
                <View style={styles.footer}>
                    <Text style={styles.footerText}>UNICEF Bolivia — Reporte confidencial de uso interno</Text>
                    <Text style={styles.footerText}>Página 1 de 2</Text>
                </View>
            </Page>

            {/* Page 2 — Detail table */}
            <Page size="A4" orientation="landscape" style={styles.page}>
                <View style={styles.header}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                        <Image src="/assets/imgs/unicef/Logo.png" style={styles.logo} />
                        <Text style={styles.title}>Detalle de donaciones</Text>
                    </View>
                    <Text style={styles.subtitle}>{periodLabel}</Text>
                </View>

                <Text style={styles.sectionTitle}>Transacciones ({(rows || []).length} registros)</Text>
                <TableSection rows={rows} />

                <View style={styles.footer}>
                    <Text style={styles.footerText}>UNICEF Bolivia — Reporte confidencial de uso interno</Text>
                    <Text style={styles.footerText}>Página 2 de 2</Text>
                </View>
            </Page>
        </Document>
    );
}

export async function downloadDashboardPDF({ summary, rows, appliedFilters }) {
    const blob = await pdf(
        <DashboardDocument summary={summary} rows={rows} appliedFilters={appliedFilters} />
    ).toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `donaciones_${appliedFilters?.dateFrom || 'reporte'}_${appliedFilters?.dateTo || ''}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
}
